import type { EquipmentItem } from '@sb/integrations/types';

import type { ValidationContext } from '../types';

import { ActionRequiredError } from './errors';

/** How the camera diagnostics name the thing that holds the reference. */
export type CameraReferenceMessaging = {
  /** Names the holder in the message, e.g. "this Locate skill", "this step". */
  subject: string;
  /** What the operator opens to fix it: "skill" or "step". */
  openTarget: string;
  /** The field the diagnostic points at. */
  fieldId: string;
};

/**
 * Resolve a camera equipment reference and validate that it still names a
 * camera this robot has. Returns the resolved equipment so a caller that needs
 * more of it — the Locate paths, which go on to check calibration — can
 * continue.
 *
 * Returns undefined only when the caller loaded no equipment: an absent list
 * means "not loaded here", not "deleted", so the reference cannot be judged and
 * the caller skips the rest of its camera checks.
 *
 * @throws {ActionRequiredError} when the reference names missing or non-camera
 *   equipment.
 */
export function resolveCameraEquipmentReference(
  ctx: ValidationContext | undefined,
  cameraEquipmentId: string,
  messaging: CameraReferenceMessaging,
): EquipmentItem | undefined {
  const equipment = ctx?.equipment;

  if (!Array.isArray(equipment)) return undefined;

  const camera = (equipment as EquipmentItem[]).find(
    (e) => e.id === cameraEquipmentId,
  );

  if (!camera) {
    throw new ActionRequiredError({
      kind: 'missingEquipment',
      message: `The camera ${messaging.subject} uses (${cameraEquipmentId}) is no longer in the equipment manager. Open the ${messaging.openTarget} and pick a camera.`,
      fieldId: messaging.fieldId,
    });
  }

  if (camera.config.kind !== 'Camera') {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `The equipment ${messaging.subject} captures through is not a camera. Open the ${messaging.openTarget} and pick a camera.`,
      fieldId: messaging.fieldId,
    });
  }

  // A disabled camera reads as "present" here — the validation equipment list
  // includes disabled items — but the runtime resolves cameras only through the
  // enabled registry (unlike grippers, camera code has no disabledEquipment
  // fallback), so it fails "not found" at play. Flag it like a deleted one.
  if (camera.isEnabled === false) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `The camera ${messaging.subject} uses (${cameraEquipmentId}) is disabled, so the routine cannot run. Enable it in the equipment manager, or open the ${messaging.openTarget} and pick another camera.`,
      fieldId: messaging.fieldId,
    });
  }

  return camera;
}

/**
 * A camera a locate path captures through must be calibrated: the runtime
 * resolves a 3D position from its intrinsics and extrinsics and fails without
 * them ("has no extrinsics or intrinsics set"). Shared by the inline Locate
 * step and the Locate skill; the color-frame steps (Inspect, Capture, Classify)
 * never call it.
 *
 * @throws {ActionRequiredError}
 */
export function checkCameraCalibration(
  camera: EquipmentItem,
  messaging: Pick<CameraReferenceMessaging, 'subject' | 'fieldId'>,
): void {
  const cfg = camera.config as {
    intrinsicsId?: string | null;
    extrinsicsId?: string | null;
  };

  if (!cfg.intrinsicsId) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `The camera ${messaging.subject} uses has no intrinsics calibration, so locate cannot resolve a position. Calibrate the camera's intrinsics.`,
      fieldId: messaging.fieldId,
    });
  }

  if (!cfg.extrinsicsId) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `The camera ${messaging.subject} uses has no extrinsics calibration, so locate cannot resolve a position. Calibrate the camera's extrinsics.`,
      fieldId: messaging.fieldId,
    });
  }
}

/** The finding for a step-args camera step that runs without a camera picked. */
export function noCameraSelectedError(fieldId: string): ActionRequiredError {
  return new ActionRequiredError({
    kind: 'invalidConfiguration',
    message:
      'This step has no camera selected, so the routine cannot run. Open the step and pick a camera.',
    fieldId,
  });
}

/**
 * The camera check for steps whose camera lives directly in their args (Capture,
 * Classify): never needs the resolved camera back, because those steps only read
 * a color frame — no intrinsics or extrinsics, unlike the inline Locate step.
 *
 * The whole check is gated on `useCppCameraBot`. Only that path reads
 * `cameraEquipmentId`; with the flag off the runtime ignores the id and uses the
 * legacy wrist camera, so neither an unset reference nor a stale/disabled
 * leftover id blocks the routine. With the flag on (the default on real boxes)
 * the id is mandatory and must resolve to an enabled camera.
 *
 * @throws {ActionRequiredError}
 */
export function checkCameraEquipmentReference(
  ctx: ValidationContext | undefined,
  cameraEquipmentId: string | null | undefined,
  fieldId: string,
): void {
  if (!ctx?.featureFlags?.useCppCameraBot) return;

  if (!cameraEquipmentId) {
    throw noCameraSelectedError(fieldId);
  }

  resolveCameraEquipmentReference(ctx, cameraEquipmentId, {
    subject: 'this step',
    openTarget: 'step',
    fieldId,
  });
}
